import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Skin } from "@esotericsoftware/spine-core";
import TickerTimeout from "../../../utils/TickerTimeout";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import PotController from "../../../Engine/PotCharging/PotController";
import { POT_CONFIG } from "../../../Engine/PotCharging/PotController";
import { PixiSettings } from "../../../pixiSettings";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { formatToCustomLocale } from "../../../utilities";
import { createTensionShakeAnimation } from "../../../utils/animations";
import FXManager from "../../../Engine/Effects/FXManager";

// Jackpot text slot mapping: spine slot name → Redux tier key
const JACKPOT_TEXT_SLOTS = {
  txt_slot_mini: "mini",
  txt_slot_minor: "minor",
  txt_slot_major: "major",
  txt_slot_grand: "grand",
};

// Maximum text width (px at base fontSize) per jackpot tier.
// Grand's frame is wider so it gets more room; the others share a tighter budget.
const JACKPOT_TEXT_MAX_WIDTH = {
  txt_slot_grand: 290,
  txt_slot_major: 260,
  txt_slot_minor: 260,
  txt_slot_mini: 260,
};

// GameView scene offset config (multiplied by symbolHeight, added to center)
// x: Positive = push RIGHT, Negative = push LEFT
// y: Positive = push DOWN,  Negative = push UP
// Same structure as GAME_CONTAINER_OFFSET_CONFIG — tune per device/orientation
// Values can be a number (y-only, backward compat) or { x, y } object
export const GAME_VIEW_OFFSET_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { x: 0, y: 0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { x: 0, y: 0 },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { x: 0, y: 0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { x: 0, y: 0 },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { x: 0, y: 0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { x: 0, y: 0 },
  },
  [PixiSettings.DEVICE.DESKTOP]: { x: 0, y: 0 },
};

/**
 * GameViewController — Unified spine scene controller.
 *
 * Manages a single `game_view` spine that contains:
 * - Background art (sky, temples, pillars, clouds)
 * - Reel frame (back, front, pillars, dividers, masks)
 * - Jackpot meters (mini, minor, major, grand, coin counter)
 * - Pot attachments (collect, double, mystery)
 *
 * External content (ReelSet, OverlayReelSet, DimReelSet) is injected into
 * bone slots via the SlotRegistry.
 *
 * Supports dual-skin compositing:
 * - Game state skin: "main" (base game) or "bonus" (Hold & Win)
 * - Layout skin: "resize/landscape" or "resize/portrait"
 * Both are combined via Spine's Skin.addSkin() and applied simultaneously.
 */
class GameViewController {
  constructor(
    container,
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    slotRegistry,
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.spine = null;

    // SlotRegistry functions for centralized injection
    this._registerSlot = slotRegistry?.registerSlot ?? null;
    this._unregisterSlot = slotRegistry?.unregisterSlot ?? null;

    // Slot containers for reel injection
    this.reelSlotContainer = null;
    this.overlayReelSlotContainer = null;

    // Current skin state
    this._currentGameState = "main";
    this._currentLayout = "resize/landscape";
    this._currentFeatureSkin = null; // e.g. "mdc", "mc", "d", etc.

    // Jackpot text instances: { slotName: { container, text } }
    this.jackpotTextInstances = {};

    // POT controllers: { potType: PotController }
    this.potControllers = {};

    // Tension effect state (bone-level offset applied to reel + pots subtrees)
    this._tensionOffset = { x: 0, y: 0, rotate: 0 };
    this._tensionController = null; // { stop, kill } from createTensionShakeAnimation
    this._tensionBones = [];

    // Active glow/anticipation overlays — synced on resize
    this._activeOverlays = new Set();

    // Rain FX container (attached to slot_attachment_rain bone)
    this._rainContainer = null;

    // Respin counter slot container (attached to txt_slot_attachment_respin_counter)
    this._respinCounterContainer = null;
    this._respinCounterBone = null;

    // Pot text labels: { M: { container, bone }, D: { ... }, C: { ... } }
    this._potTextLabels = {};

    this.initializeSpine();
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  initializeSpine() {
    this.spine = this.createSpine();
    if (!this.spine) {

      return;
    }

    // Validate
    if (!this.spine.skeleton || !this.spine.state) {

      return;
    }

    // Setup
    this.spine.position.set(0, 0);
    this.spine.zIndex = 1;
    this.spine.visible = true;

    // Add to container
    this.container.addChild(this.spine);

    // Apply initial dual-skin
    this.applyDualSkin("main", this.resolveLayoutSkin());

    // Play the base_idle animation (looping)
    this.playBaseIdle();

    // Apply responsive scaling
    this.applyResponsiveScaling();

    // Cache shake bone references (reel + pots subtrees, NOT jackpots)
    this._cacheTensionBones();

    // Initialize POT visual placeholders at bone positions
    this.initializePots();

    // Inject feature text labels under pots
    this.initializePotTextLabels();

    // Attach slot containers for reel injection
    this.attachSlotContainers();

    // Pre-upload atlas textures to GPU
    requestAnimationFrame(() => {
      SpineAtlasUploader.upload("game_view");
    });

  }

  createSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "game_view-skeleton";
      const atlasAlias = "game_view-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {

        return null;
      }

      // Create independent spine instance
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify the spine was created properly
      // Note: skeleton and state are siblings on the Spine display object, NOT nested
      if (!spine || !spine.skeleton || !spine.state) {

        return null;
      }

      // Verify skeleton has required data
      if (!spine.skeleton.data || !spine.skeleton.data.animations) {

        return null;
      }

      return spine;
    } catch (error) {

      return null;
    }
  }

  // ============================================
  // DUAL-SKIN SYSTEM
  // ============================================

  /**
   * Apply two skins simultaneously via Skin.addSkin().
   * The "default" skin provides base attachments (reel frame, jackpot frames, etc.)
   * Game state skin ("main" or "bonus") adds background art.
   * Layout skin ("resize/landscape" or "resize/portrait") adds responsive positioning.
   *
   * @param {string} gameState — "main" or "bonus"
   * @param {string} layout — "resize/landscape" or "resize/portrait"
   */
  applyDualSkin(gameState, layout) {
    if (!this.spine?.skeleton?.data) return;

    const skeletonData = this.spine.skeleton.data;

    // Find base skins
    const defaultSkin = skeletonData.findSkin("default");
    const gameStateSkin = skeletonData.findSkin(gameState);
    const layoutSkin = skeletonData.findSkin(layout);

    if (!defaultSkin) {

      return;
    }

    // Build combined skin
    const combined = new Skin("combined");
    combined.addSkin(defaultSkin);
    if (gameStateSkin) combined.addSkin(gameStateSkin);
    if (layoutSkin) combined.addSkin(layoutSkin);

    // Apply
    this.spine.skeleton.setSkin(combined);
    this.spine.skeleton.setSlotsToSetupPose();

    // Cache current state
    this._currentGameState = gameState;
    this._currentLayout = layout;
  }

  /**
   * Switch game state skin (main ↔ bonus).
   * Layout skin stays the same.
   */
  setGameState(gameState) {
    if (gameState === this._currentGameState) return;
    // Skip if a transition animation is handling the swap
    if (this._transitionActive) return;
    this.applyDualSkin(gameState, this._currentLayout);

    // Switch idle animation to match game state
    if (gameState === "bonus") {
      this.playFeatureIdle();
    } else {
      this.playBaseIdle();
    }
  }

  /**
   * Switch layout skin based on orientation.
   * Game state skin stays the same.
   */
  setOrientation(orientation) {
    const layout = orientation === PixiSettings.ORIENTATION.PORTRAIT
      ? "resize/portrait"
      : "resize/landscape";
    if (layout === this._currentLayout) return;
    this.applyDualSkin(this._currentGameState, layout);
  }

  /**
   * Resolve the layout skin name from current device/orientation.
   */
  resolveLayoutSkin() {
    if (this.device === PixiSettings.DEVICE.DESKTOP) return "resize/landscape";
    return this.orientation === PixiSettings.ORIENTATION.PORTRAIT
      ? "resize/portrait"
      : "resize/landscape";
  }

  // ============================================
  // IDLE ANIMATIONS
  // ============================================

  /**
   * Play base_idle animation (for base game) on track 0.
   * Mirrors BackgroundAnimationController.playBaseIdle() pattern.
   */
  playBaseIdle() {
    if (!this.spine?.state) return;
    try {
      if (this._hasAnimation("base_idle")) {
        this.spine.state.setAnimation(0, "base_idle", true);
      }
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Play feature_idle animation (for Hold and Win feature) on track 0.
   * Mirrors BackgroundAnimationController.playFeatureIdle() pattern.
   * Guarded: skips if feature_idle is already the active animation on track 0
   * to prevent restarting from frame 0 (which causes a brief cloud flash).
   */
  playFeatureIdle() {
    if (!this.spine?.state) return;
    try {
      if (this._hasAnimation("feature_idle")) {
        // Skip if feature_idle is already playing — avoid restarting from frame 0
        const current = this.spine.state.getCurrent(0);
        if (current?.animation?.name === "feature_idle") return;
        this.spine.state.setAnimation(0, "feature_idle", true);
      }
    } catch (error) {
      // Fail silently
    }
  }

  // ============================================
  // RESPONSIVE SCALING
  // ============================================

  /**
   * Apply responsive scaling and positioning.
   * Same algorithm as BackgroundAnimationController.applyResponsiveBackgroundScaling():
   * - Scale: Math.min(1, symbolScale) — only scale down when symbols shrink
   * - Position: center X, center Y + device-specific offset
   */
  applyResponsiveScaling() {
    if (!this.dimensions || !this.spine) return;

    const { width, height } = this.dimensions;

    // Scale spine to match symbol scaling (ultra-wide cap handled by DimensionsContext)
    const backgroundScale = this.symbolScale;

    // Device/orientation offset — supports { x, y } object or plain number (y-only)
    const deviceConfig = GAME_VIEW_OFFSET_CONFIG[this.device];
    const resolvedConfig =
      typeof deviceConfig === "number" || (deviceConfig && typeof deviceConfig.x === "number")
        ? deviceConfig
        : (deviceConfig?.[this.orientation] ?? { x: 0, y: 0 });

    // Backward compat: plain number = y-only offset
    const offsetX = typeof resolvedConfig === "number" ? 0 : (resolvedConfig.x ?? 0);
    const offsetY = typeof resolvedConfig === "number" ? resolvedConfig : (resolvedConfig.y ?? 0);

    const positionX = width / 2 + this.symbolHeight * offsetX;
    const positionY = height / 2 + this.symbolHeight * offsetY;

    this.spine.scale.set(backgroundScale);
    this.spine.position.set(positionX, positionY);
  }

  // ============================================
  // RESIZE
  // ============================================

  resize(dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;

    this.applyResponsiveScaling();

    // Re-evaluate layout skin if orientation changed
    this.setOrientation(orientation);

    // Sync any active overlays (GJP anticipation, reel glow) to new transform + skin
    this._syncActiveOverlays();
  }

  /**
   * Sync all active overlay spines to match the main spine's transform and skin.
   * Called on resize/orientation change so overlays follow in real time.
   */
  _syncActiveOverlays() {
    if (!this.spine || this._activeOverlays.size === 0) return;

    for (const overlay of this._activeOverlays) {
      if (!overlay || overlay.destroyed) {
        this._activeOverlays.delete(overlay);
        continue;
      }

      // Sync position and scale to main spine
      overlay.scale.copyFrom(this.spine.scale);
      overlay.position.copyFrom(this.spine.position);

      // Re-apply dual skin for the new layout orientation
      const skeletonData = overlay.skeleton?.data;
      if (skeletonData) {
        const defaultSkin = skeletonData.findSkin("default");
        const gameStateSkin = skeletonData.findSkin(this._currentGameState);
        const layoutSkin = skeletonData.findSkin(this._currentLayout);

        if (defaultSkin) {
          const overlaySkin = new Skin("overlay_resized");
          overlaySkin.addSkin(defaultSkin);
          if (gameStateSkin) overlaySkin.addSkin(gameStateSkin);
          if (layoutSkin) overlaySkin.addSkin(layoutSkin);
          overlay.skeleton.setSkin(overlaySkin);
          overlay.skeleton.setToSetupPose();
        }
      }
    }
  }

  // ============================================
  // SLOT CONTAINER INJECTION
  // ============================================

  /**
   * Attach containers for reel injection.
   *
   * Draw order in spine (higher = on top):
   *   ReelSet needs to render BELOW pillars but ABOVE main_reel_bg.
   *   OverlayReelSet renders above everything via addSlotObject.
   *
   * Problem: attachment_slot_placeholder draws ABOVE pillars.
   * Fix: ReelSet uses MANUAL bone-following with forced zIndex
   *       placed between main_reel_bg and main_reel_frame.
   *       OverlayReelSet stays at its slot via addSlotObject (above everything).
   */
  attachSlotContainers() {
    if (!this.spine?.skeleton) return;

    // ─── ReelSet: manual bone-following (below pillars) ───
    const reelBone = this.spine.skeleton.findBone("attachment_slot_placeholder");
    if (reelBone) {
      this.reelSlotContainer = new PIXI.Container();
      this.reelSlotContainer.name = "gameview_reel_slot_content";
      this.reelSlotContainer.sortableChildren = true;
      // Dynamically place between main_reel_bg and main_reel_frame
      const reelBgIndex = this._getSlotIndex("main_reel_bg");
      this.reelSlotContainer.zIndex = reelBgIndex + 0.5;
      this.spine.addChild(this.reelSlotContainer);

      // Sync container transform to bone every frame
      this._reelBone = reelBone;
      this.spine.afterUpdateWorldTransforms = (spineInstance) => {
        this._syncBoneToContainer(this._reelBone, this.reelSlotContainer);
      };

      if (this._registerSlot && FEATURES_CONFIG.SLOT_REGISTRY.GAME_VIEW_INJECTION) {
        this._registerSlot("gameView.reelSet", this.reelSlotContainer);
        this._registerSlot("gameView.tensionReelSet", this.reelSlotContainer);
      }
    }

    // ─── OverlayReelSet + TensionReelSet: addSlotObject at slot (above pillars) ───
    const overlaySlot = this.spine.skeleton.findSlot("attachment_slot_placeholder_front");
    if (overlaySlot) {
      this.overlayReelSlotContainer = new PIXI.Container();
      this.overlayReelSlotContainer.name = "gameview_overlay_reel_slot_content";
      this.overlayReelSlotContainer.sortableChildren = true;
      this.spine.addSlotObject("attachment_slot_placeholder_front", this.overlayReelSlotContainer);

      if (this._registerSlot && FEATURES_CONFIG.SLOT_REGISTRY.GAME_VIEW_INJECTION) {
        this._registerSlot("gameView.overlayReelSet", this.overlayReelSlotContainer);
      }
    }

    // ─── Rain FX: inject at slot_attachment_rain (hidden until bonus) ───
    // Same pattern as coin counter: create once at init, bone-sync each frame,
    // toggle visibility at transition midpoint.
    const rainSlot = this.spine.skeleton.findSlot("slot_attachment_rain");
    if (rainSlot?.bone) {
      this._rainContainer = new PIXI.Container();
      this._rainContainer.name = "rain_fx_container";
      this._rainContainer.visible = false; // Hidden until bonus entry
      const rainSlotIndex = this._getSlotIndex("slot_attachment_rain");
      this._rainContainer.zIndex = rainSlotIndex + 0.5;
      this.spine.addChild(this._rainContainer);
      this._rainBone = rainSlot.bone;
    }

    // ─── Respin Counter: inject at txt_slot_attachment_respin_counter (hidden until bonus) ───
    // PotCollectionRespinCounter.jsx injects its spine child via SlotRegistry.
    const respinCounterSlot = this.spine.skeleton.findSlot("txt_slot_attachment_respin_counter");
    if (respinCounterSlot?.bone) {
      this._respinCounterContainer = new PIXI.Container();
      this._respinCounterContainer.name = "respin_counter_slot";
      this._respinCounterContainer.alpha = 0.001; // Rendered but invisible until bonus
      const rcSlotIndex = this._getSlotIndex("txt_slot_attachment_respin_counter");
      this._respinCounterContainer.zIndex = rcSlotIndex + 0.5;
      this.spine.addChild(this._respinCounterContainer);
      this._respinCounterBone = respinCounterSlot.bone;

      if (this._registerSlot && FEATURES_CONFIG.POT_COLLECTION.ENABLED) {
        this._registerSlot("gameView.respinCounter", this._respinCounterContainer);
      }
    } else {

    }
  }

  /**
   * Sync a container's transform to a bone's world transform.
   * Mirrors what spine-pixi's updateSlotObject does, but without zIndex override.
   */
  _syncBoneToContainer(bone, container) {
    if (!bone || !container) return;
    const transform = container.localTransform;
    transform.a = bone.a;
    transform.b = bone.c;
    transform.c = -bone.b;
    transform.d = -bone.d;
    transform.tx = bone.worldX;
    transform.ty = bone.worldY;
    container.transform.setFromMatrix(transform);
    // Keep forced zIndex (don't let spine override it)
  }

  /**
   * Get the draw-order index of a slot by name.
   * Used to compute dynamic zIndex values that survive spine re-exports
   * (where slot counts and indices change).
   * @param {string} slotName - Slot name to look up
   * @returns {number} The slot's index in the draw order, or 0 if not found
   */
  _getSlotIndex(slotName) {
    if (!this.spine?.skeleton) return 0;
    const slot = this.spine.skeleton.findSlot(slotName);
    if (!slot) return 0;
    return this.spine.skeleton.slots.indexOf(slot);
  }

  // ============================================
  // POT CONTROLLERS
  // ============================================

  /**
   * Create PotController instances for all 3 pot types and inject into bone positions.
   * Each controller renders the pots.json spine with the matching skin
   * (collect/double/mystery) and manages 12-stage growth progression.
   */
  initializePots() {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;
    if (!this.spine?.skeleton) return;

    // Preserve stage state across re-init (e.g. resize)
    const savedStages = {};
    for (const [potType, controller] of Object.entries(this.potControllers)) {
      savedStages[potType] = controller.stage ?? 0;
    }

    // Destroy existing controllers
    for (const controller of Object.values(this.potControllers)) {
      controller.destroy();
    }
    this.potControllers = {};

    for (const potType of Object.keys(POT_CONFIG)) {
      const controller = new PotController(potType, this.spine);
      if (!controller.bone) {

        controller.destroy();
        continue;
      }

      this.spine.addChild(controller.container);

      // Restore saved stage or default to idle at stage 0
      const savedStage = savedStages[potType];
      if (savedStage > 0) {
        controller.setStage(savedStage);
      } else {
        controller.playIdle();
      }

      this.potControllers[potType] = controller;
    }

    // Reinstall bone sync to include new pot controllers
    this._installJackpotBoneSync();

  }

  /**
   * Update pot controllers' spine reference after game_view spine re-creation (resize).
   * @param {object} newSpine - The newly created game_view spine instance
   */
  updatePotsSpineRef(newSpine) {
    if (!FEATURES_CONFIG.POT_CHARGING.ENABLED) return;
    for (const controller of Object.values(this.potControllers)) {
      controller.updateSpineRef(newSpine);
    }
  }

  /**
   * Inject feature text label images into the pot text attachment slots.
   * Each pot has a corresponding text slot in the game_view spine:
   *   M (Mystery) → attachment_pot_mystery_txt
   *   D (Double)  → attachment_pot_double_txt
   *   C (Collect) → attachment_pot_collect_txt
   *
   * Creates PIXI Sprites from preloaded feature images and syncs them
   * to their bone positions via _installJackpotBoneSync.
   *
   * In base game: all visible. In bonus: only active features visible.
   */
  initializePotTextLabels() {
    if (!this.spine?.skeleton) return;

    // Clean up existing labels (e.g. on resize re-init)
    for (const label of Object.values(this._potTextLabels)) {
      if (label.container && !label.container.destroyed) {
        if (label.container.parent) label.container.parent.removeChild(label.container);
        label.container.destroy({ children: true });
      }
    }
    this._potTextLabels = {};

    const labelConfig = {
      M: { slotName: "attachment_pot_mystery_txt", textureName: "pot_mystery_txt" },
      D: { slotName: "attachment_pot_double_txt",  textureName: "pot_double_txt" },
      C: { slotName: "attachment_pot_collect_txt", textureName: "pot_collect_txt" },
    };

    for (const [key, config] of Object.entries(labelConfig)) {
      const slot = this.spine.skeleton.findSlot(config.slotName);
      if (!slot?.bone) {
        continue;
      }

      // Get preloaded texture
      let texture = null;
      try {
        if (PIXI.Assets.cache.has(config.textureName)) {
          texture = PIXI.Assets.get(config.textureName);
        }
      } catch (_) {}

      if (!texture) {
        continue;
      }

      // Create sprite centered on bone position
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5, 0.5);

      // Wrap in container for bone sync
      const container = new PIXI.Container();
      container.name = `pot_text_label_${key}`;
      container.addChild(sprite);

      // Position in spine draw order (same z-level as the slot)
      const slotIndex = this._getSlotIndex(config.slotName);
      container.zIndex = slotIndex + 0.5;

      this.spine.addChild(container);

      this._potTextLabels[key] = {
        container,
        bone: slot.bone,
        sprite,
      };
    }

    // Show all labels by default (base game state)
    this.showAllPotTexts();

    // Reinstall bone sync to include text labels
    this._installJackpotBoneSync();
  }

  // ============================================
  // CLEANUP
  // ============================================

  destroy() {
    // Stop tension effect immediately (no ramp-out)
    this._killTensionEffect();

    // Clean up any in-flight transition overlay
    if (this._transitionOverlay) {
      this._destroyTransitionOverlay(this._transitionOverlay);
      this._transitionOverlay = null;
      this._transitionActive = false;
    }

    // Clean up any lingering overlays (GJP anticipation/win, glow, etc.)
    for (const overlay of this._activeOverlays) {
      if (overlay && !overlay.destroyed) {
        try {
          overlay.state?.clearListeners();
          overlay.state?.clearTracks();
          if (overlay.parent) overlay.parent.removeChild(overlay);
          overlay.destroy();
        } catch (_) {}
      }
    }
    this._activeOverlays.clear();

    // Clean up POT controllers
    for (const controller of Object.values(this.potControllers)) {
      controller.destroy();
    }
    this.potControllers = {};

    // Clean up pot text labels
    for (const label of Object.values(this._potTextLabels)) {
      if (label.container && !label.container.destroyed) {
        if (label.container.parent) label.container.parent.removeChild(label.container);
        label.container.destroy({ children: true });
      }
    }
    this._potTextLabels = {};

    // Clean up jackpot text instances
    for (const slotName of Object.keys(this.jackpotTextInstances)) {
      this.destroyJackpotText(slotName);
    }

    if (this._unregisterSlot) {
      this._unregisterSlot("gameView.reelSet");
      this._unregisterSlot("gameView.overlayReelSet");
      this._unregisterSlot("gameView.tensionReelSet");
    }

    // Clean up rain FX
    this._stopRain();

    if (this.spine) {
      this.spine.destroy();
      this.spine = null;
    }

    this.reelSlotContainer = null;
    this.overlayReelSlotContainer = null;
    this._tensionBones = [];
  }

  // ============================================
  // JACKPOT TEXT INJECTION
  // ============================================

  /**
   * Return a PIXI.TextStyle for jackpot text that fits within the given maxWidth.
   * Measures the text at base fontSize; if wider than the budget, scales fontSize
   * down proportionally so the text always fits inside the jackpot frame.
   * @param {string} displayValue - Formatted jackpot value string
   * @param {number} maxWidth - Maximum allowed text width in pixels
   * @returns {PIXI.TextStyle}
   */
  _fittedJackpotStyle(displayValue, maxWidth) {
    const base = FONT_STYLES.jackpots;
    const dpr = window.devicePixelRatio || 1;
    let fontSize = base.fontSize;
    const style = new PIXI.TextStyle({ ...scaleStyle(base, fontSize), resolution: dpr });
    const metrics = PIXI.TextMetrics.measureText(displayValue, style);
    if (metrics.width > maxWidth) {
      fontSize = Math.round((fontSize * (maxWidth / metrics.width)) / 2) * 2; // Snap to even
      return new PIXI.TextStyle({ ...scaleStyle(base, fontSize), resolution: dpr });
    }
    return style;
  }

  /**
   * Create PIXI.Text instances for all 4 jackpot tiers using bone-following.
   * Uses addChild + _syncBoneToContainer (same as ReelSet) instead of
   * addSlotObject which disrupts spine's internal draw order.
   * @param {Object} jackpotValues - { mini, minor, major, grand } in subunits
   */
  initializeJackpotTexts(jackpotValues) {
    if (!this.spine?.skeleton) return;

    for (const [slotName, tier] of Object.entries(JACKPOT_TEXT_SLOTS)) {
      const slot = this.spine.skeleton.findSlot(slotName);
      if (!slot?.bone) continue;

      // Destroy existing text if any
      this.destroyJackpotText(slotName);

      const value = jackpotValues?.[tier] ?? 0;
      const displayValue = formatToCustomLocale(value);

      const maxWidth = JACKPOT_TEXT_MAX_WIDTH[slotName] || 270;

      const container = new PIXI.Container();
      container.name = `${slotName}_text`;
      // Render above jackpot frame art — dynamically resolved
      const jackpotFrameIndex = this._getSlotIndex(slotName);
      container.zIndex = jackpotFrameIndex + 1;

      // Shrink-to-fit: scale fontSize down if text is wider than the frame budget.
      const fittedStyle = this._fittedJackpotStyle(displayValue, maxWidth);
      const text = new PIXI.Text(displayValue, fittedStyle);
      text.anchor.set(0.5);
      text.roundPixels = true;

      container.addChild(text);
      this.spine.addChild(container);
      this.jackpotTextInstances[slotName] = { container, text, bone: slot.bone, maxWidth };
    }

    // Extend the afterUpdateWorldTransforms callback to sync jackpot bones
    this._installJackpotBoneSync();
  }

  /**
   * Install/update the afterUpdateWorldTransforms callback to include
   * jackpot bone syncing alongside the existing reel bone sync.
   */
  _installJackpotBoneSync() {
    this.spine.afterUpdateWorldTransforms = (spineInstance) => {
      // 1. Apply shake offsets to reel + pots bones BEFORE any container syncs
      if (this._tensionOffset && this._tensionBones.length > 0) {
        const dx = this._tensionOffset.x || 0;
        const dy = this._tensionOffset.y || 0;
        if (dx !== 0 || dy !== 0) {
          for (const bone of this._tensionBones) {
            bone.worldX += dx;
            bone.worldY += dy;
          }
        }
      }

      // 2. Sync reel bone to container (picks up shake offset)
      if (this._reelBone && this.reelSlotContainer) {
        this._syncBoneToContainer(this._reelBone, this.reelSlotContainer);
      }

      // 3. Sync jackpot text containers to their bones (NOT shaken)
      // for-in over the plain object avoids an Object.values() array alloc every frame
      for (const key in this.jackpotTextInstances) {
        const instance = this.jackpotTextInstances[key];
        if (instance.bone && instance.container && !instance.container.destroyed) {
          this._syncBoneToContainer(instance.bone, instance.container);
        }
      }

      // 4. Sync POT controllers to their bones (shaken via pots subtree)
      for (const key in this.potControllers) {
        const controller = this.potControllers[key];
        if (controller.bone && controller.container && !controller.container.destroyed) {
          this._syncBoneToContainer(controller.bone, controller.container);
        }
      }

      // 5. Sync pot text labels to their bones (shaken via pots subtree)
      for (const key in this._potTextLabels) {
        const label = this._potTextLabels[key];
        if (label.bone && label.container && !label.container.destroyed) {
          this._syncBoneToContainer(label.bone, label.container);
        }
      }

      // 6. Sync coin counter to its bone (NOT shaken — child of jackpots)
      if (this._coinCounterInstance?.bone && this._coinCounterInstance?.container && !this._coinCounterInstance.container.destroyed) {
        this._syncBoneToContainer(this._coinCounterInstance.bone, this._coinCounterInstance.container);
      }

      // 7. Sync rain FX container to its bone
      if (this._rainBone && this._rainContainer && !this._rainContainer.destroyed) {
        this._syncBoneToContainer(this._rainBone, this._rainContainer);
      }

      // 8. Sync respin counter container to its bone
      if (this._respinCounterBone && this._respinCounterContainer && !this._respinCounterContainer.destroyed) {
        this._syncBoneToContainer(this._respinCounterBone, this._respinCounterContainer);
      }
    };
  }

  /**
   * Update existing jackpot text values (no-op if text unchanged).
   * Falls back to initializeJackpotTexts if any text instance is missing/destroyed.
   * @param {Object} jackpotValues - { mini, minor, major, grand } in subunits
   */
  updateJackpotValues(jackpotValues) {
    if (!jackpotValues) return;

    for (const [slotName, tier] of Object.entries(JACKPOT_TEXT_SLOTS)) {
      const value = jackpotValues[tier] ?? 0;
      const displayValue = formatToCustomLocale(value);
      const instance = this.jackpotTextInstances[slotName];

      if (instance?.text && !instance.text.destroyed) {
        if (instance.text.text !== displayValue) {
          instance.text.text = displayValue;
          // Refit font size for the new value using stored per-slot max width
          const maxWidth = instance.maxWidth || 270;
          instance.text.style = this._fittedJackpotStyle(displayValue, maxWidth);
        }
      } else {
        // Text doesn't exist — create all
        this.initializeJackpotTexts(jackpotValues);
        return; // initializeJackpotTexts handles all slots
      }
    }
  }

  /**
   * Destroy a single jackpot text instance by slot name.
   * @param {string} slotName - Spine slot name (e.g. "txt_slot_mini")
   */
  destroyJackpotText(slotName) {
    const instance = this.jackpotTextInstances[slotName];
    if (!instance) return;

    if (instance.text && !instance.text.destroyed) {
      instance.text.destroy();
    }
    if (instance.container && !instance.container.destroyed) {
      if (instance.container.parent) {
        instance.container.parent.removeChild(instance.container);
      }
      instance.container.destroy({ children: true });
    }
    delete this.jackpotTextInstances[slotName];
  }

  // ============================================
  // COIN COUNTER (Pot Collection — X/15 display)
  // ============================================

  /**
   * Initialize the coin counter text at the `txt_slot_coin_counter_value` bone.
   * Uses the same bone-sync pattern as jackpot texts.
   * @param {number} count - Current coin count
   * @param {number} total - Max coins (15)
   */
  initializeCoinCounter(count = 0, total = 15) {
    if (!this.spine?.skeleton) {

      return;
    }

    // Clean up existing
    this.destroyCoinCounter();

    const slotName = "txt_slot_coin_counter_value";
    const slot = this.spine.skeleton.findSlot(slotName);

    if (!slot?.bone) {

      return;
    }

    const displayValue = `${count}/${total}`;

    // Same container + zIndex pattern as jackpot texts
    const container = new PIXI.Container();
    container.name = "coin_counter_text";
    const slotIndex = this._getSlotIndex(slotName);
    container.zIndex = slotIndex + 1;

    // WinText style, proportionally scaled down from native 150px
    const scaledStyle = scaleStyle(FONT_STYLES.winText, 48);
    const style = new PIXI.TextStyle(scaledStyle);

    const text = new PIXI.Text(displayValue, style);
    text.anchor.set(0.5);
    text.roundPixels = true;

    container.addChild(text);
    this.spine.addChild(container);

    this._coinCounterInstance = { container, text, bone: slot.bone };

    // Reinstall bone sync to include coin counter
    this._installJackpotBoneSync();
  }

  /**
   * Update the coin counter text.
   * @param {number} count - Current coin count
   * @param {number} total - Max coins (default 15)
   */
  updateCoinCounter(count, total = 15) {
    const instance = this._coinCounterInstance;
    if (!instance?.text || instance.text.destroyed) {
      this.initializeCoinCounter(count, total);
      return;
    }
    const newText = `${count}/${total}`;
    if (instance.text.text !== newText) {
      instance.text.text = newText;
    }
  }

  /**
   * Show/hide the coin counter.
   * @param {boolean} visible
   */
  setCoinCounterVisible(visible) {
    if (this._coinCounterInstance?.container) {
      this._coinCounterInstance.container.visible = visible;
    }
  }

  /**
   * Destroy coin counter text.
   */
  destroyCoinCounter() {
    const instance = this._coinCounterInstance;
    if (!instance) return;

    if (instance.text && !instance.text.destroyed) {
      instance.text.destroy();
    }
    if (instance.container && !instance.container.destroyed) {
      if (instance.container.parent) {
        instance.container.parent.removeChild(instance.container);
      }
      instance.container.destroy({ children: true });
    }
    this._coinCounterInstance = null;
  }

  /**
   * Show/hide the respin counter container.
   * @param {boolean} visible - true to show (alpha 1), false to hide (alpha 0.001)
   */
  setRespinCounterVisible(visible) {
    if (this._respinCounterContainer) {
      this._respinCounterContainer.alpha = visible ? 1 : 0.001;
    }
  }

  // ============================================
  // POT TEXT LABEL VISIBILITY
  // ============================================

  /**
   * Set the active feature skin for pot text labels.
   * Controls visibility of injected pot text sprites.
   * Pass feature string like "MDC", "MC", "D" etc. to show matching labels.
   * Pass null to hide all labels.
   *
   * @param {string|null} skinName - Feature string or null
   */
  setFeatureSkin(skinName) {
    this._currentFeatureSkin = skinName || null;

    // Toggle visibility of pot text label sprites
    const features = (skinName || "").toUpperCase();
    for (const [key, label] of Object.entries(this._potTextLabels)) {
      if (label.container && !label.container.destroyed) {
        label.container.visible = features.includes(key);
      }
    }
  }

  /**
   * Show all pot text labels (all features visible).
   * Used when returning to base game.
   */
  showAllPotTexts() {
    this.setFeatureSkin("MDC");
  }

  /**
   * Hide all pot text labels.
   */
  hideAllPotTexts() {
    this.setFeatureSkin(null);
  }

  // ============================================
  // TENSION EFFECT (reel + pots, NOT jackpots)
  // ============================================

  /**
   * Cache all bones in the reel and pots subtrees for tension effect.
   * Called once at init. These are the bones whose worldX/worldY will be
   * modified in afterUpdateWorldTransforms during the tension effect.
   */
  _cacheTensionBones() {
    this._tensionBones = [];
    if (!this.spine?.skeleton) return;

    const collectSubtree = (rootName) => {
      const rootBone = this.spine.skeleton.findBone(rootName);
      if (!rootBone) return;
      const collect = (bone) => {
        this._tensionBones.push(bone);
        if (bone.children) {
          for (const child of bone.children) {
            collect(child);
          }
        }
      };
      collect(rootBone);
    };

    collectSubtree("reel");
    collectSubtree("pots");
  }

  /**
   * Start tension effect on reel + pots bones.
   * Creates a shake animation with built-in ramp in/out envelope.
   * Bone offsets are applied in afterUpdateWorldTransforms every frame.
   *
   * @param {number} symbolHeight - Current symbol height for responsive scaling
   */
  startTensionEffect(symbolHeight) {
    // Kill any existing tension
    if (this._tensionController) {
      this._tensionController.kill();
      this._tensionController = null;
    }

    // Reset offset proxy
    this._tensionOffset = { x: 0, y: 0, rotate: 0 };

    // Create animation with built-in ramp in (returns controller with stop/kill)
    this._tensionController = createTensionShakeAnimation(this._tensionOffset, symbolHeight);
  }

  /**
   * Stop tension effect with gradual ramp-out (handled by the animation).
   */
  stopTensionEffect() {
    if (this._tensionController) {
      const stoppingController = this._tensionController;
      this._tensionController = null; // Detach before ramp-out starts
      stoppingController.stop(() => {
        // Only reset offset if no new tension has started
        if (!this._tensionController) {
          this._tensionOffset = { x: 0, y: 0, rotate: 0 };
        }
      });
    } else {
      this._tensionOffset = { x: 0, y: 0, rotate: 0 };
    }
  }

  /**
   * Immediately kill tension effect (no ramp-out, for destroy/cleanup).
   */
  _killTensionEffect() {
    if (this._tensionController) {
      this._tensionController.kill();
      this._tensionController = null;
    }
    this._tensionOffset = { x: 0, y: 0, rotate: 0 };
  }

  // ============================================
  // ANTICIPATION ANIMATIONS (GDD §10.2, §10.3)
  // ============================================

  /**
   * Expected animation names in game_view.json:
   * - "anticipation_in"   — one-shot intro (storm builds)
   * - "anticipation_loop" — looping state (thunder/lightning active)
   * - "anticipation_out"  — one-shot outro (storm fades)
   *
   * All play on track 0 with crossfade so the idle bones blend smoothly.
   * If animations don't exist yet, methods silently no-op.
   */

  /**
   * Check if a specific animation exists in the spine skeleton data.
   * @param {string} animName
   * @returns {boolean}
   */
  _hasAnimation(animName) {
    if (!this.spine?.skeleton?.data) return false;
    return this.spine.skeleton.data.findAnimation(animName) !== null;
  }

  /**
   * Start anticipation sequence: crossfade from current idle into anticipation.
   *
   * Anticipation animations key the same bones as base_idle (clouds, sky, etc.)
   * plus storm effects, so they're full-scene replacements — NOT overlays.
   * Playing on track 0 with crossfade ensures zero visual flash.
   *
   * Flow: base_idle → (crossfade) → anticipation_in → anticipation_loop
   * @param {Function} [onIntroComplete] - Called when anticipation_in finishes (loop starts)
   */
  playAnticipation(onIntroComplete) {
    if (!this.spine?.state) return;

    const hasIntro = this._hasAnimation("anticipation_in");
    const hasLoop = this._hasAnimation("anticipation_loop");

    if (!hasIntro && !hasLoop) {
      return;
    }

    if (hasIntro) {
      // Play anticipation intro sound alongside the intro animation
      soundManager.play(SOUND_NAME.feature_anticipation_in);

      // Crossfade from base_idle into anticipation_in on track 0
      const introEntry = this.spine.state.setAnimation(0, "anticipation_in", false);
      introEntry.mixDuration = 0.5; // Smooth blend from base_idle

      // Queue loop after intro completes
      if (hasLoop) {
        const loopEntry = this.spine.state.addAnimation(0, "anticipation_loop", true, 0);
        loopEntry.mixDuration = 0.2;
      }

      // When intro completes, transition sound from intro to loop
      introEntry.listener = {
        complete: () => {
          soundManager.stop(SOUND_NAME.feature_anticipation_in);
          soundManager.play(SOUND_NAME.feature_anticipation_loop);
          if (onIntroComplete) onIntroComplete();
        },
      };
    } else if (hasLoop) {
      // No intro — start loop sound directly
      soundManager.play(SOUND_NAME.feature_anticipation_loop);

      const loopEntry = this.spine.state.setAnimation(0, "anticipation_loop", true);
      loopEntry.mixDuration = 0.5;

      if (onIntroComplete) onIntroComplete();
    }
  }

  /**
   * Stop anticipation: play outro then crossfade back to idle.
   *
   * Flow: anticipation_loop → (crossfade) → anticipation_out → (crossfade) → base_idle/feature_idle
   */
  stopAnticipation() {
    if (!this.spine?.state) return;

    // Stop anticipation sounds (in case intro is still playing)
    soundManager.stop(SOUND_NAME.feature_anticipation_in);
    soundManager.stop(SOUND_NAME.feature_anticipation_loop);

    // Determine which idle to return to based on current game state
    const returnIdle = this._currentGameState === "bonus" ? "feature_idle" : "base_idle";
    const hasReturnIdle = this._hasAnimation(returnIdle);
    const hasOutro = this._hasAnimation("anticipation_out");

    if (hasOutro) {
      // Play outro sound alongside the outro animation
      soundManager.play(SOUND_NAME.feature_anticipation_out);

      // Crossfade from loop into outro
      const outroEntry = this.spine.state.setAnimation(0, "anticipation_out", false);
      outroEntry.mixDuration = 0.3;

      // Queue return to idle after outro completes
      if (hasReturnIdle) {
        const idleEntry = this.spine.state.addAnimation(0, returnIdle, true, 0);
        idleEntry.mixDuration = 0.5;
      }
    } else if (hasReturnIdle) {
      // No outro — crossfade directly back to idle
      const idleEntry = this.spine.state.setAnimation(0, returnIdle, true);
      idleEntry.mixDuration = 0.5;
    }
  }

  // ============================================
  // GJP WIN OVERLAY (Pot Collection — 15th coin)
  // ============================================

  /**
   * Play the GJP win celebration as a SEPARATE spine overlay.
   * Creates its own overlay (independent from anticipation overlay)
   * so both can coexist or transition independently.
   *
   * Flow: gjp_anticipation_win_loop_in → gjp_anticipation_win_loop
   */
  playGjpWin() {
    // Create a fresh overlay for the win animation
    const winOverlay = this._createAnticipationOverlay();
    if (!winOverlay) return;

    this._gjpWinOverlay = winOverlay;
    this._gjpWinActive = true;

    const hasIntro = winOverlay.skeleton.data.findAnimation('gjp_anticipation_win_loop_in');
    if (hasIntro) {
      winOverlay.state.setAnimation(0, 'gjp_anticipation_win_loop_in', false);
      winOverlay.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === 'gjp_anticipation_win_loop_in' && this._gjpWinActive) {
            winOverlay.state.setAnimation(0, 'gjp_anticipation_win_loop', true);
          }
        },
      });
    } else {
      // No intro — jump straight to loop
      winOverlay.state.setAnimation(0, 'gjp_anticipation_win_loop', true);
    }
  }

  /**
   * Stop the GJP win overlay.
   * Plays win_loop_out → destroys overlay on complete.
   */
  stopGjpWin() {
    this._gjpWinActive = false;

    const winOverlay = this._gjpWinOverlay;
    if (!winOverlay) return;
    // Null ref immediately so concurrent calls can't double-destroy
    this._gjpWinOverlay = null;

    const hasOutro = winOverlay.skeleton?.data?.findAnimation('gjp_anticipation_win_loop_out');
    if (hasOutro) {
      winOverlay.state.clearListeners();
      winOverlay.state.setAnimation(0, 'gjp_anticipation_win_loop_out', false);
      winOverlay.state.addListener({
        complete: () => {
          this._destroyTransitionOverlay(winOverlay);
        },
      });
    } else {
      this._destroyTransitionOverlay(winOverlay);
    }
  }

  // ============================================
  // REEL GLOW (End-of-Bonus — GDD §22.5)
  // ============================================

  /**
   * Play Bonus_bg_reel_activation as a SEPARATE spine overlay (same pattern as Transition).
   *
   * Like Transition, this animation keys shared bones/slots and cannot be played on the
   * main game_view spine without breaking the current idle. Solution: create a temporary
   * spine overlay, play the one-shot glow, then destroy it.
   *
   * @returns {Promise<void>} Resolves when glow animation completes (or immediately if unavailable)
   */
  playReelGlow() {
    if (!this.spine?.state) return Promise.resolve();

    const animName = "Bonus_bg_reel_activation";
    if (!this._hasAnimation(animName)) {

      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Create overlay spine (same skeleton/atlas, matched transform)
      const overlaySpine = this._createGlowOverlay();
      if (!overlaySpine) {
        resolve();
        return;
      }

      // Play the glow animation
      soundManager.play(SOUND_NAME.gjp_reel_glow);
      overlaySpine.state.setAnimation(0, animName, false);

      const listener = {
        complete: () => {
          overlaySpine.state.removeListener(listener);
          this._destroyTransitionOverlay(overlaySpine); // same cleanup as transition
          resolve();
        },
      };
      overlaySpine.state.addListener(listener);

      // Safety timeout in case callback doesn't fire
      const safetyTimeout = new TickerTimeout(() => {
        if (overlaySpine.parent) {
          overlaySpine.state.removeListener(listener);
          this._destroyTransitionOverlay(overlaySpine);
        }
        resolve();
      }, 5000);

      // Clear safety timeout if spine completes normally
      const originalComplete = listener.complete;
      listener.complete = () => {
        safetyTimeout.clear();
        if (originalComplete) originalComplete();
      };
    });
  }

  /**
   * Play bonus_end as a SEPARATE spine overlay (same pattern as playReelGlow).
   *
   * Fires at end-of-bonus when grid is NOT full (no Grand Jackpot).
   * Creates a temporary spine overlay, plays the one-shot animation, then destroys it.
   *
   * @returns {Promise<void>} Resolves when bonus_end animation completes
   */
  playBonusEnd() {
    if (!this.spine?.state) return Promise.resolve();

    const animName = "bonus_end";
    if (!this._hasAnimation(animName)) {

      return Promise.resolve();
    }

    return new Promise((resolve) => {
      // Create overlay spine (same skeleton/atlas, matched transform)
      const overlaySpine = this._createGlowOverlay();
      if (!overlaySpine) {
        resolve();
        return;
      }

      // Play the bonus_end animation and sound
      soundManager.play(SOUND_NAME.bonus_end);
      overlaySpine.state.setAnimation(0, animName, false);

      const listener = {
        complete: () => {
          overlaySpine.state.removeListener(listener);
          this._destroyTransitionOverlay(overlaySpine);
          resolve();
        },
      };
      overlaySpine.state.addListener(listener);

      // Safety timeout in case callback doesn't fire
      const safetyTimeout = new TickerTimeout(() => {
        if (overlaySpine.parent) {
          overlaySpine.state.removeListener(listener);
          this._destroyTransitionOverlay(overlaySpine);
        }
        resolve();
      }, 5000);

      // Clear safety timeout if spine completes normally
      const originalComplete = listener.complete;
      listener.complete = () => {
        safetyTimeout.clear();
        if (originalComplete) originalComplete();
      };
    });
  }

  /**
   * Get the Grand Jackpot label position in global coordinates.
   * Used by the SpinEngine to trail the GJP value from the label to the collector.
   * @returns {{ x: number, y: number } | null}
   */
  getGrandJackpotLabelPosition() {
    if (!this.spine?.skeleton) return null;
    const bone = this.spine.skeleton.findBone("txt_slot_grand");
    if (!bone) return null;
    bone.updateWorldTransform();
    return this.spine.toGlobal({ x: bone.worldX, y: bone.worldY });
  }

  /**
   * Create a lightweight spine overlay for the reel glow effect.
   * Same approach as _createTransitionOverlay but uses the current bonus skin
   * (we're already in the bonus scene when the glow plays).
   * @returns {Spine|null}
   */
  _createGlowOverlay() {
    try {
      const overlaySpine = Spine.from({
        skeleton: "game_view-skeleton",
        atlas: "game_view-atlas",
      });

      if (!overlaySpine?.skeleton || !overlaySpine?.state) return null;

      // Exact copy of main spine transform — same as _createAnticipationOverlay.
      // No scale or Y corrections needed; the animation is authored at the same scale.
      overlaySpine.scale.copyFrom(this.spine.scale);
      overlaySpine.position.copyFrom(this.spine.position);
      // Render above the main spine
      overlaySpine.zIndex = this.spine.zIndex + 1000;
      overlaySpine.visible = true;

      // Use the current dual-skin (bonus + layout) so the glow aligns with the active scene
      const skeletonData = overlaySpine.skeleton.data;
      const defaultSkin = skeletonData.findSkin("default");
      const gameStateSkin = skeletonData.findSkin(this._currentGameState);
      const layoutSkin = skeletonData.findSkin(this._currentLayout);

      if (defaultSkin) {
        const overlaySkin = new Skin("overlay_glow");
        overlaySkin.addSkin(defaultSkin);
        if (gameStateSkin) overlaySkin.addSkin(gameStateSkin);
        if (layoutSkin) overlaySkin.addSkin(layoutSkin);
        overlaySpine.skeleton.setSkin(overlaySkin);
        overlaySpine.skeleton.setSlotsToSetupPose();
      }

      this.container.addChild(overlaySpine);
      this._activeOverlays.add(overlaySpine);
      return overlaySpine;
    } catch (error) {

      return null;
    }
  }

  /**
   * Create a game_view spine overlay for GJP anticipation animations.
   * Copies the main spine transform EXACTLY — no scale or Y adjustments.
   * Kept separate from _createGlowOverlay which has reel-glow-specific corrections.
   */
  _createAnticipationOverlay() {
    try {
      const overlaySpine = Spine.from({
        skeleton: "game_view-skeleton",
        atlas: "game_view-atlas",
      });

      if (!overlaySpine?.skeleton || !overlaySpine?.state) return null;

      // Exact copy of main spine transform — no scale multiplier, no Y offset.
      // GJP anticipation is authored at the same scale as the main game_view spine.
      overlaySpine.scale.copyFrom(this.spine.scale);
      overlaySpine.position.copyFrom(this.spine.position);
      overlaySpine.zIndex = this.spine.zIndex + 1000;
      overlaySpine.visible = true;

      // Apply the same dual-skin as the main spine so bones are in the correct positions
      const skeletonData = overlaySpine.skeleton.data;
      const defaultSkin = skeletonData.findSkin("default");
      const gameStateSkin = skeletonData.findSkin(this._currentGameState);
      const layoutSkin = skeletonData.findSkin(this._currentLayout);

      if (defaultSkin) {
        const overlaySkin = new Skin("overlay_anticipation");
        overlaySkin.addSkin(defaultSkin);
        if (gameStateSkin) overlaySkin.addSkin(gameStateSkin);
        if (layoutSkin) overlaySkin.addSkin(layoutSkin);
        overlaySpine.skeleton.setSkin(overlaySkin);
        overlaySpine.skeleton.setToSetupPose();
      }

      this.container.addChild(overlaySpine);
      this._activeOverlays.add(overlaySpine);
      return overlaySpine;
    } catch (error) {

      return null;
    }
  }

  // ============================================
  // TRANSITION (Skin Swap With Cloud Cover)
  // ============================================

  /**
   * Play the Transition animation as a SEPARATE spine instance overlaid on top.
   *
   * The Transition animation lives in the same skeleton data as base_idle/feature_idle,
   * and it keys every shared bone (88/88) and slot (144/144). This means it CANNOT
   * be played on the same spine instance — whether on track 0 (replaces idle visuals)
   * or track 1 (higher track overrides shared slots → NULLs 120 attachments → black screen).
   *
   * Solution: create a second Spine.from() instance from the same skeleton/atlas,
   * position it identically, and play only the Transition animation on it.
   * The main game_view spine continues playing its idle undisturbed underneath.
   *
   * At the "transition_midpoint" spine event (t=1.0s, baked into game_view.json),
   * clouds fully cover the screen — we swap skin + idle on the MAIN spine.
   * When Transition completes, we destroy the overlay spine.
   *
   * Flow:
   *   Main spine (track 0):  base_idle ──── [midpoint: swap skin + target_idle] ──── target_idle
   *   Overlay spine (track 0): ── Transition (clouds cover + reveal) ── complete → destroy
   *
   * @param {string} targetState — "bonus" or "main"
   * @param {Function} [onMidpoint] — optional callback fired when screen is fully covered
   */
  playTransition(targetState, onMidpoint, onComplete) {
    if (!this.spine?.state) return;

    // Guard against double-fire while a transition is already running
    if (this._transitionActive) return;

    const hasTransition = this._hasAnimation("Transition");
    if (!hasTransition) {
      // No transition animation — instant swap fallback
      this.applyDualSkin(targetState, this._currentLayout);
      if (targetState === "bonus") this.playFeatureIdle();
      else this.playBaseIdle();
      if (onMidpoint) onMidpoint();
      if (onComplete) onComplete();
      return;
    }

    this._transitionActive = true;

    // Create a separate spine instance for the overlay
    const overlaySpine = this._createTransitionOverlay();
    if (!overlaySpine) {
      // Failed to create overlay — fall back to instant swap
      this.applyDualSkin(targetState, this._currentLayout);
      if (targetState === "bonus") this.playFeatureIdle();
      else this.playBaseIdle();
      if (onMidpoint) onMidpoint();
      if (onComplete) onComplete();
      this._transitionActive = false;
      return;
    }
    // Track for cleanup if destroy() is called mid-transition
    this._transitionOverlay = overlaySpine;

    // Play Transition on the overlay spine
    overlaySpine.state.setAnimation(0, "Transition", false);
    soundManager.play(SOUND_NAME.transition);

    // Listen for the "transition_midpoint" event (t=1.0s in game_view.json)
    const listener = {
      event: (trackEntry, event) => {
        if (event.data.name !== "transition_midpoint") return;

        // Swap skin + idle on the MAIN spine (hidden under cloud cover)
        this.applyDualSkin(targetState, this._currentLayout);
        const targetIdle = targetState === "bonus" ? "feature_idle" : "base_idle";
        if (this._hasAnimation(targetIdle)) {
          this.spine.state.setAnimation(0, targetIdle, true);
        }

        if (onMidpoint) onMidpoint();

        // Rain FX: start on bonus entry, stop on return to main
        if (targetState === "bonus") {
          this._startRain();
        } else {
          this._stopRain();
          // Cleanup: destroy any lingering bonus overlays (GJP anticipation/win)
          // that may still be alive under cloud cover (e.g. loop_out still playing)
          for (const overlay of this._activeOverlays) {
            if (overlay && overlay !== overlaySpine && !overlay.destroyed) {
              try {
                overlay.state?.clearListeners();
                overlay.state?.clearTracks();
                if (overlay.parent) overlay.parent.removeChild(overlay);
                overlay.destroy();
              } catch (_) {}
            }
          }
          // Keep only the transition overlay itself in the set
          this._activeOverlays.clear();
          this._activeOverlays.add(overlaySpine);
        }
      },
      complete: (trackEntry) => {
        // Transition finished — destroy the overlay, reveal new idle underneath
        overlaySpine.state.removeListener(listener);
        this._destroyTransitionOverlay(overlaySpine);
        this._transitionOverlay = null;
        this._transitionActive = false;
        if (onComplete) onComplete();
      },
    };
    overlaySpine.state.addListener(listener);
  }

  /**
   * Create a separate spine instance for the Transition overlay.
   * Copies position/scale from the main spine, renders ABOVE it via zIndex.
   * @returns {Spine|null} The overlay spine, or null on failure
   */
  _createTransitionOverlay() {
    try {
      const overlaySpine = Spine.from({
        skeleton: "game_view-skeleton",
        atlas: "game_view-atlas",
      });

      if (!overlaySpine?.skeleton || !overlaySpine?.state) return null;

      // Match the main spine's transform exactly
      overlaySpine.scale.copyFrom(this.spine.scale);
      overlaySpine.position.copyFrom(this.spine.position);
      // Render above the main spine
      overlaySpine.zIndex = this.spine.zIndex + 1000;
      overlaySpine.visible = true;

      // Always use "main" game state skin for transition overlay — the main skin's
      // Transition animation has fuller cloud coverage. Using the bonus skin produces
      // thinner clouds that don't fully cover the screen during the swap.
      const skeletonData = overlaySpine.skeleton.data;
      const defaultSkin = skeletonData.findSkin("default");
      const mainSkin = skeletonData.findSkin("main");
      const layoutSkin = skeletonData.findSkin(this._currentLayout);

      if (defaultSkin) {
        const overlaySkin = new Skin("overlay_transition");
        overlaySkin.addSkin(defaultSkin);
        if (mainSkin) overlaySkin.addSkin(mainSkin);
        if (layoutSkin) overlaySkin.addSkin(layoutSkin);
        overlaySpine.skeleton.setSkin(overlaySkin);
        overlaySpine.skeleton.setSlotsToSetupPose();
      }

      this.container.addChild(overlaySpine);
      return overlaySpine;
    } catch (error) {

      return null;
    }
  }

  /**
   * Destroy the Transition overlay spine and remove from container.
   * @param {Spine} overlaySpine
   */
  _destroyTransitionOverlay(overlaySpine) {
    if (!overlaySpine) return;
    this._activeOverlays.delete(overlaySpine);
    if (overlaySpine.parent) {
      overlaySpine.parent.removeChild(overlaySpine);
    }
    overlaySpine.destroy();
  }

  // ============================================
  // RAIN FX (Pot Collection Bonus)
  // ============================================

  /**
   * Start rain particle effect — just show the pre-injected container and start FX.
   * Container was created in attachSlotContainers at the correct spine draw order.
   */
  _startRain() {
    if (!FXManager.isReady || FXManager.isRainPlaying) return;
    if (!this._rainContainer) return;

    this._rainContainer.visible = true;
    FXManager.startRain(this._rainContainer);

  }

  /**
   * Stop rain particle effect — stop FX and hide the container.
   */
  _stopRain() {
    FXManager.stopRain();
    if (this._rainContainer) {
      this._rainContainer.visible = false;
    }
  }
}

export default GameViewController;
